import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ShieldCheck, ShieldX, Loader2, Search, Play, Sparkles, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Public certificate verification page (/verify, /verify/:certId).
// Anyone can paste a GameView Certificate ID to confirm a game's authenticity & creator.
export default function VerifyCertificate() {
  const { certId } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(certId || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const verify = useCallback(async (id) => {
    const cid = (id || "").trim().toUpperCase().replace(/\s+/g, "");
    if (!cid) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await fetch(`${BACKEND}/api/og/verify/${encodeURIComponent(cid)}`);
      setResult(await r.json());
    } catch {
      setResult({ found: false, certificate_id: cid });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (certId) { setInput(certId); verify(certId); } }, [certId, verify]);

  const submit = (e) => {
    e.preventDefault();
    const cid = input.trim().toUpperCase().replace(/\s+/g, "");
    if (cid) navigate(`/verify/${encodeURIComponent(cid)}`);
    else verify(input);
  };

  return (
    <div className="min-h-screen bg-[#05060a] text-white flex flex-col items-center px-6 py-14" data-testid="verify-page">
      <img src="/brand/gv-wordmark.png" alt="GameView Studio" className="h-9 mb-10 opacity-90 cursor-pointer" onClick={() => navigate("/")} />
      <div className="w-full max-w-xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold mb-3">
            <Award className="w-3.5 h-3.5" /> CERTIFICATE VERIFICATION
          </div>
          <h1 className="text-3xl font-extrabold">Verify a Certificate of Authenticity</h1>
          <p className="text-white/60 mt-2">Paste a GameView certificate ID (e.g. <span className="font-mono text-white/80">GV-XXXX-XXXX-XXXX</span>) to confirm a game's authenticity and creator.</p>
        </div>

        <form onSubmit={submit} className="flex gap-2">
          <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="GV-XXXX-XXXX-XXXX"
            className="bg-white/5 border-white/15 text-white placeholder:text-white/30 font-mono uppercase" data-testid="verify-input" />
          <Button type="submit" disabled={loading} className="rounded-xl bg-gradient-to-r from-[#1f6feb] to-[#7c3aed] shrink-0" data-testid="verify-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Verify
          </Button>
        </form>

        <div className="mt-8">
          {loading && <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin text-[#7c5cff]" /></div>}

          {!loading && result?.found && (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-6" data-testid="verify-valid">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-emerald-500 grid place-items-center shrink-0"><ShieldCheck className="w-7 h-7 text-white" /></div>
                <div>
                  <div className="text-lg font-extrabold text-emerald-400">Authentic certificate</div>
                  <div className="text-xs text-white/50 font-mono">{result.certificate_id}</div>
                </div>
              </div>
              <div className="rounded-xl bg-black/25 p-4 space-y-2.5 text-sm">
                <Row label="Game" value={<span>{result.game.cover_emoji} {result.game.title}</span>} testid="verify-game-title" />
                <Row label="Creator" value={result.creator.name} />
                <Row label="Licence" value={result.licence_label} />
                <Row label="Provenance" value={`AI-built · ${result.ai_generated_assets}/${result.total_assets} assets`} />
                <Row label="IP scan" value={<span className="text-emerald-400 font-semibold">Passed — no protected IP</span>} />
                <Row label="Issued" value={(result.issued_at || "").slice(0, 10)} />
                {result.remix && <Row label="Remix of" value={`${result.remix.source_title}${result.remix.source_creator ? ` by ${result.remix.source_creator}` : ""}`} />}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-5">
                <Button onClick={() => navigate(`/g/${result.game.id}`)} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#7c3aed]" data-testid="verify-play-btn">
                  <Play className="w-4 h-4" /> View & play this game
                </Button>
                <Button onClick={() => navigate("/register")} variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/10">
                  <Sparkles className="w-4 h-4" /> Build your own with AI
                </Button>
              </div>
            </div>
          )}

          {!loading && result && !result.found && (
            <div className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.06] p-6 text-center" data-testid="verify-invalid">
              <div className="w-12 h-12 rounded-full bg-rose-500/90 grid place-items-center mx-auto mb-3"><ShieldX className="w-7 h-7 text-white" /></div>
              <div className="text-lg font-extrabold text-rose-400">No matching certificate</div>
              <p className="text-white/60 mt-1 text-sm">We couldn't find a published game with certificate <span className="font-mono">{result.certificate_id}</span>. Check the ID and try again.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-white/50">{label}</span>
      <span className="font-semibold text-right" data-testid={testid}>{value}</span>
    </div>
  );
}
